'use client'

/**
 * <ai-feedback> — student-triggered AI feedback on handwritten/annotated work.
 *
 * Three input paths:
 * 1. Button: collects the student's own annotation strokes (and content
 *    images) that currently sit between the previous h1/h2 and this component
 *    (live DOM positions; nothing below the component), renders them to a PNG
 *    (render-strokes-to-png.ts) and sends it.
 * 2. Paste zone: hover/focus the dashed box and press Ctrl+V with a screenshot
 *    in the clipboard — sends the pasted image instead. Covers work on top of
 *    tables/SVG/plugins where we can't rasterize the underlying DOM.
 * 3. Camera/file: pick or shoot a photo (capture="environment" opens the rear
 *    camera on mobile). Same normalize path as paste — for work done on paper.
 *
 * The server re-derives the exercise text, teacher prompt and reference
 * solution (solution="...") from page content; the client only sends pageId,
 * feedbackId and the image, and never receives the prompt or solution attrs.
 *
 * Persistence: the last successful feedback (text + a small JPEG thumbnail of
 * what was sent) is saved per tag via useSyncedUserData under
 * `ai-feedback-<id>` (or `ai-feedback-idx<n>` by DOM order when the tag has no
 * id), so it survives a reload and syncs like other student data. Only a
 * completed answer is saved; an aborted/failed request keeps the previous one.
 * Index-keyed records drift if the teacher inserts another <ai-feedback> above.
 *
 * Print: nothing; every printout carries a QR code to the page on its
 * first page (print-page-qr.tsx). A link with `?fb=<id or index>` still scrolls to
 * this tag and highlights the "take a picture" button (the camera itself
 * can't open without a tap); no printed code links there any more.
 * Index links drift like index-keyed records (see above).
 *
 * Section scoping duplicates the live-position math of
 * section-anchored-strokes.tsx in read-only form: a stroke's current y =
 * stored y + (live section top − stored sectionOffsetY).
 *
 * @see src/app/api/ai/feedback/route.ts
 * @see src/lib/ai/feedback-context.ts
 */

import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import { Sparkles, Loader2, AlertCircle, ClipboardPaste, Camera } from 'lucide-react'
import { PrintFallback } from '@/components/print/print-fallback'
import { userDataService, useSyncedUserData } from '@/lib/userdata'
import type { AIFeedbackData, AnnotationData } from '@/lib/userdata/types'
import { parseStrokes } from '@/hooks/use-stroke-animation'
import {
  renderStrokesToPng,
  type RenderableImage,
  type RenderableStroke,
} from '@/lib/annotations/render-strokes-to-png'
import type { ChatStreamEvent } from '@/lib/ai/types'
import { feedbackText, type FeedbackMark } from '@/lib/ai/feedback-marks'
import { AIFeedbackMarks } from './ai-feedback-marks'
import { reportScore } from '@/lib/plugin-share'

interface AIFeedbackProps {
  pageId?: string
  feedbackId?: string
  label?: string
}

/** Cap pasted screenshots to this edge length (px) before upload. */
const MAX_PASTE_EDGE = 2000

/** Above this, pasted/photo images go as JPEG; the route caps at 6M chars. */
const MAX_PNG_CHARS = 5_500_000

/** Ignore images smaller than this (paper px) — inline icons, emoji. */
const MIN_IMAGE_EDGE = 32

/**
 * Reload an image with crossOrigin="anonymous" so drawing it can never taint
 * the capture canvas. Same-origin (/api/files/…) always succeeds; S3 images
 * succeed only if the bucket sends CORS headers. Returns null on failure —
 * the caller skips that image rather than losing the whole capture.
 */
function loadCorsSafeImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = src
  })
}

/**
 * Max edge (px) of the persisted image. Large enough to read the work under
 * the AI's marks (display is max-h-[32rem]); ~100–250 KB as JPEG q0.7.
 */
const THUMB_EDGE = 1200

/**
 * Downscale the sent image to a small JPEG for persistence (the full PNG can
 * be MBs). White background because stroke PNGs are transparent. Returns null
 * when canvas/image decoding is unavailable — feedback text is saved anyway.
 */
async function makeThumbnail(dataUrl: string): Promise<string | null> {
  const canvas = document.createElement('canvas')
  let ctx: CanvasRenderingContext2D | null = null
  try {
    ctx = canvas.getContext('2d')
  } catch {
    return null
  }
  if (!ctx) return null
  const img = await new Promise<HTMLImageElement | null>((resolve) => {
    const el = new Image()
    el.onload = () => resolve(el)
    el.onerror = () => resolve(null)
    el.src = dataUrl
  })
  if (!img || !img.naturalWidth || !img.naturalHeight) return null
  const scale = Math.min(1, THUMB_EDGE / Math.max(img.naturalWidth, img.naturalHeight))
  canvas.width = Math.round(img.naturalWidth * scale)
  canvas.height = Math.round(img.naturalHeight * scale)
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  return canvas.toDataURL('image/jpeg', 0.7)
}

type Status = 'idle' | 'preparing' | 'streaming'

export function AIFeedback({ pageId, feedbackId, label }: AIFeedbackProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  // Paste is armed while the paste box is hovered OR focused (two flags, so
  // leaving with the mouse doesn't disarm a focused box).
  const pasteHoverRef = useRef(false)
  const pasteFocusRef = useRef(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [status, setStatus] = useState<Status>('idle')
  const [feedback, setFeedback] = useState('')
  // Shown while the server continues an answer that hit the token limit
  // (route.ts sends `continuing`); cleared when text resumes.
  const [continuing, setContinuing] = useState(false)
  const [sentImage, setSentImage] = useState<string | null>(null)
  const [liveMarks, setLiveMarks] = useState<FeedbackMark[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Storage key: the author's id, else this tag's DOM index (resolved after
  // mount, when all sibling tags exist). Until then the hook stays dormant
  // (pageId '' sentinel).
  const [storageKey, setStorageKey] = useState<string | null>(
    feedbackId ? `ai-feedback-${feedbackId}` : null
  )
  // Link target of the printed QR code (`?fb=`): the id, else the DOM index.
  const [linkKey, setLinkKey] = useState<string | null>(feedbackId ?? null)
  const [linked, setLinked] = useState(false)
  useEffect(() => {
    const index = containerRef.current
      ? [...document.querySelectorAll('[data-ai-feedback]')].indexOf(containerRef.current)
      : -1
    const key = feedbackId ?? (index >= 0 ? String(index) : null)
    setLinkKey(key)
    setStorageKey(feedbackId ? `ai-feedback-${feedbackId}` : index >= 0 ? `ai-feedback-idx${index}` : null)
    // Opened from the printed QR code: bring this tag into view.
    const fb = new URLSearchParams(window.location.search).get('fb')
    if (key !== null && fb === key && !new URLSearchParams(window.location.search).has('print')) {
      setLinked(true)
      // After layout settles (images, editors above may still grow).
      const t = window.setTimeout(() => containerRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 600)
      return () => window.clearTimeout(t)
    }
  }, [feedbackId])
  const { data: saved, updateData: saveFeedback } = useSyncedUserData<AIFeedbackData>(
    storageKey && pageId ? pageId : '',
    storageKey ?? 'ai-feedback',
    null
  )

  // The "nothing written" error goes stale once the student draws; the
  // annotation layer announces every stroke change. Listens for the whole
  // lifetime: attaching only while `error` is set left a gap between the error
  // rendering and the effect running, in which a stroke event was lost.
  // setError(null) without an error is a no-op re-render bail-out.
  // @see src/components/annotations/annotation-layer.tsx (eduskript:annotations-changed)
  useEffect(() => {
    const clear = () => setError(null)
    window.addEventListener('eduskript:annotations-changed', clear)
    return () => window.removeEventListener('eduskript:annotations-changed', clear)
  }, [])

  const busy = status !== 'idle'

  // Live request wins; otherwise fall back to the persisted last feedback.
  const showLive = busy || !!feedback
  const shownFeedback = showLive ? feedback : saved?.feedback ?? ''
  const shownImage = showLive ? sentImage : saved?.image ?? null
  // The answer ends in a ```marks block of quotes (feedback-marks.ts); it is
  // hidden here. The text streams as usual; the located marks arrive as a
  // separate event about 1 s after it.
  const shownText = feedbackText(shownFeedback, status !== 'streaming')
  const marks = (showLive ? liveMarks : saved?.marks) ?? []

  const sendImage = async (image: string) => {
    if (!pageId) {
      setError('AI feedback needs a saved page context.')
      return
    }
    setError(null)
    setFeedback('')
    setContinuing(false)
    setLiveMarks(null)
    setSentImage(image)
    setStatus('streaming')

    // Which <ai-feedback> tag on the page is this? DOM order matches source
    // order, so the server can pick the right prompt/section without the
    // teacher having to assign ids.
    const feedbackIndex = containerRef.current
      ? [...document.querySelectorAll('[data-ai-feedback]')].indexOf(containerRef.current)
      : -1

    try {
      const response = await fetch('/api/ai/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageId, feedbackId, feedbackIndex, image }),
      })
      if (!response.ok) {
        const data = await response.json().catch(() => null)
        throw new Error(data?.error || 'Request failed')
      }
      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response stream')

      const decoder = new TextDecoder()
      let buffer = ''
      let accumulated = ''
      let located: FeedbackMark[] = []
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? '' // keep incomplete line for next chunk
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          let event: ChatStreamEvent
          try {
            event = JSON.parse(line.slice(6)) as ChatStreamEvent
          } catch {
            continue
          }
          if (event.type === 'content' && event.content) {
            accumulated += event.content
            setFeedback(accumulated)
            setContinuing(false)
          } else if (event.type === 'marks' && event.marks) {
            located = event.marks
            setLiveMarks(located)
            if (event.score != null && storageKey) reportScore({ componentId: storageKey, earned: event.score })
          } else if (event.type === 'continuing') {
            setContinuing(true)
          } else if (event.type === 'truncated') {
            // Retrying wouldn't help: the same drawing needs the same budget.
            accumulated += "…\n\n*Even my spare tokens ran out, this one's too much for me. Sorry!*"
            setFeedback(accumulated)
          } else if (event.type === 'error') {
            throw new Error(event.error || 'AI error')
          }
        }
      }
      if (!accumulated) {
        setError('The AI returned no feedback. Please try again.')
      } else if (storageKey && pageId) {
        try {
          const thumb = await makeThumbnail(image)
          await saveFeedback(
            { feedback: feedbackText(accumulated, true), image: thumb, marks: located, savedAt: Date.now() },
            { immediate: true }
          )
        } catch (saveErr) {
          // Feedback is on screen; losing persistence shouldn't surface as an error.
          console.warn('[ai-feedback] could not save feedback:', saveErr)
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setStatus('idle')
      setContinuing(false)
    }
  }

  /** Button path: strokes between the previous h1/h2 and this component → PNG → send. */
  const handleFeedbackClick = async () => {
    if (busy) return
    if (!pageId) {
      setError('AI feedback needs a saved page context.')
      return
    }
    setError(null)
    setStatus('preparing')
    try {
      const paperEl = document.getElementById('paper')
      const hostEl = containerRef.current
      if (!paperEl || !hostEl) {
        setError('Could not locate the page content.')
        return
      }

      const paperRect = paperEl.getBoundingClientRect()
      // Strokes are stored in unscaled layout px; divide client coords by the
      // current zoom/pan scale to compare against them (see POSITIONING.md).
      const scale = paperRect.width / paperEl.offsetWidth || 1
      const toPaperY = (clientY: number) => (clientY - paperRect.top) / scale

      // Capture bounds: previous h1/h2 (or paper top) → top of this component.
      // Nothing below the component is included, so an example answer or the
      // next exercise placed after the button stays out. Must stay in step with
      // HEADING_RE + the tag-line cutoff in src/lib/ai/feedback-context.ts,
      // which scopes the markdown sent alongside this image — if the two
      // disagree, the model is shown strokes from one range and text from
      // another.
      const componentTop = toPaperY(hostEl.getBoundingClientRect().top)
      let yTop = 0
      const yBottom = componentTop
      for (const el of paperEl.querySelectorAll('[data-section-id^="h1-"], [data-section-id^="h2-"]')) {
        const top = toPaperY(el.getBoundingClientRect().top)
        if (top <= componentTop && top > yTop) yTop = top
      }

      // The annotation layer autosaves 2s after the last stroke; without this
      // flush a student who writes and clicks immediately reads a stale record
      // and gets the misleading "nothing written here yet" error.
      // @see src/components/annotations/annotation-layer.tsx (eduskript:flush-annotations)
      const flush: { waitFor: Promise<void>[] } = { waitFor: [] }
      window.dispatchEvent(new CustomEvent('eduskript:flush-annotations', { detail: flush }))
      await Promise.allSettled(flush.waitFor)

      const record = await userDataService.get<AnnotationData>(pageId, 'annotations')
      const strokes = parseStrokes(record?.data?.canvasData)

      const included: RenderableStroke[] = []
      for (const stroke of strokes) {
        if (stroke.mode !== 'draw' || stroke.points.length < 2) continue
        const sectionEl = stroke.sectionId
          ? paperEl.querySelector(`[data-section-id="${CSS.escape(stroke.sectionId)}"]`)
          : null
        // Orphaned sections render at stored coords (annotation-svg-layer
        // fallback), so shift 0 matches what the student sees.
        const yShift = sectionEl
          ? toPaperY(sectionEl.getBoundingClientRect().top) - (stroke.sectionOffsetY ?? 0)
          : 0
        let sum = 0
        for (const p of stroke.points) sum += p.y
        const liveAvgY = sum / stroke.points.length + yShift
        if (liveAvgY >= yTop && liveAvgY < yBottom) {
          included.push({
            points: stroke.points,
            mode: stroke.mode,
            // Theme ink: the capture is drawn on white (light paper), so
            // ink is black there, also when the student sees it white.
            color: stroke.color === 'currentColor' ? '#000000' : stroke.color,
            width: stroke.width,
            yShift,
          })
        }
      }

      // Composite images (content images, excalidraw SVGs) that sit in the
      // section, so drawing ON an image needs no screenshot. Live rects from
      // the same instant as the stroke shifts keep both aligned.
      const toPaperX = (clientX: number) => (clientX - paperRect.left) / scale
      const imageCandidates: Array<{ src: string; x: number; y: number; w: number; h: number }> = []
      for (const el of paperEl.querySelectorAll('img')) {
        if (el.closest('[data-ai-feedback]')) continue // thumbnails of any ai-feedback block
        const r = el.getBoundingClientRect()
        const w = r.width / scale
        const h = r.height / scale
        if (w < MIN_IMAGE_EDGE || h < MIN_IMAGE_EDGE) continue // hidden or icon-sized
        const centerY = toPaperY(r.top) + h / 2
        if (centerY < yTop || centerY >= yBottom) continue
        // The capture is drawn on white, so use the light variant of themed
        // images (plot/molecule/excalidraw render a -light and a -dark <img>;
        // in dark mode only the dark one has a size).
        const darkClass = [...el.classList].find((c) => /^(plot|molecule|excalidraw)-dark$/.test(c))
        const light = darkClass
          ? el.parentElement?.querySelector<HTMLImageElement>(`img.${darkClass.replace(/-dark$/, '-light')}`)
          : null
        const src = light ? light.currentSrc || light.src : el.currentSrc || el.src
        imageCandidates.push({ src, x: toPaperX(r.left), y: toPaperY(r.top), w, h })
      }
      const images: RenderableImage[] = []
      for (const cand of imageCandidates) {
        const source = await loadCorsSafeImage(cand.src)
        if (source) {
          images.push({ source, x: cand.x, y: cand.y, w: cand.w, h: cand.h })
        } else {
          console.warn('[ai-feedback] skipping non-CORS image in capture:', cand.src)
        }
      }

      const png = renderStrokesToPng(included, images)
      if (!png) {
        setError(
          'Nothing written in this section yet. Write your solution on the page first, or paste a screenshot into the box instead.'
        )
        return
      }
      await sendImage(png.dataUrl)
    } finally {
      setStatus((s) => (s === 'preparing' ? 'idle' : s))
    }
  }

  /**
   * Paste/camera path: normalize the image to a capped PNG data URL.
   *
   * Phone camera photos carry an EXIF orientation flag; canvas drawImage does
   * NOT apply it, so a portrait shot lands sideways. createImageBitmap with
   * `imageOrientation: 'from-image'` bakes the rotation into the bitmap's
   * pixels (and its width/height come out already-oriented). Fall back to
   * <img> (which some browsers auto-orient, some don't) if the bitmap path is
   * unavailable.
   */
  const handlePastedBlob = async (blob: Blob) => {
    try {
      let source: ImageBitmap | HTMLImageElement
      let srcW: number
      let srcH: number
      try {
        source = await createImageBitmap(blob, { imageOrientation: 'from-image' })
        srcW = source.width
        srcH = source.height
      } catch {
        const url = URL.createObjectURL(blob)
        try {
          source = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image()
            el.onload = () => resolve(el)
            el.onerror = () => reject(new Error('Could not read the image'))
            el.src = url
          })
          srcW = source.naturalWidth
          srcH = source.naturalHeight
        } finally {
          URL.revokeObjectURL(url)
        }
      }

      const scale = Math.min(1, MAX_PASTE_EDGE / Math.max(srcW, srcH))
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(srcW * scale)
      canvas.height = Math.round(srcH * scale)
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Could not process the image')
      ctx.drawImage(source, 0, 0, canvas.width, canvas.height)
      if (source instanceof ImageBitmap) source.close()
      // Photos with sensor noise compress badly as PNG (a 1500×2000 phone
      // photo came to 7.3M chars, over the route's 6M cap); JPEG then.
      let dataUrl = canvas.toDataURL('image/png')
      if (dataUrl.length > MAX_PNG_CHARS) dataUrl = canvas.toDataURL('image/jpeg', 0.85)
      await sendImage(dataUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read the image')
    }
  }

  // Document-level paste listener, only active while the drop zone is
  // hovered or focused (pasteHoverRef/pasteFocusRef) — same scoping idea as the snap paste
  // handler, but per-component so multiple <ai-feedback> tags don't all fire.
  // The listener is bound once and calls the latest render's handler through
  // this ref; binding it with a stale closure lost the first paste's save
  // (storageKey was still null in the first render).
  const pasteRef = useRef<(blob: Blob) => void>(() => {})
  useEffect(() => {
    pasteRef.current = (blob) => { if (!busy) void handlePastedBlob(blob) }
  })
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (!pasteHoverRef.current && !pasteFocusRef.current) return
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const blob = item.getAsFile()
          if (blob) {
            // Swallowed even while busy (then ignored): otherwise it reaches
            // the snap paste handler (paste-snap-handler.tsx, document,
            // bubble phase; we listen in capture) and opens the snap dialog.
            e.preventDefault()
            e.stopImmediatePropagation()
            pasteRef.current(blob)
            return
          }
        }
      }
    }
    document.addEventListener('paste', onPaste, true)
    return () => document.removeEventListener('paste', onPaste, true)
  }, [])

  // The wrapper with data-ai-feedback renders in print too, so the DOM index
  // (request routing, storage key, QR link) is the same in both modes.
  return (
    <div ref={containerRef} data-ai-feedback={feedbackId || true}>
    {/* Prints nothing: the printout's page QR code links here (print-page-qr.tsx). */}
    <PrintFallback label="AI feedback" empty>
    <div className="my-6 not-prose">
      {linked && (
        <p className="mb-2 flex items-center gap-2 text-sm font-medium text-primary">
          <Camera className="h-4 w-4" /> Take a picture of your solution on paper:
        </p>
      )}
      <div className="flex flex-wrap items-stretch gap-3">
        <button
          type="button"
          onClick={handleFeedbackClick}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {status === 'streaming' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          {label || 'Get AI feedback'}
        </button>

        <div
          tabIndex={0}
          role="button"
          aria-label="Paste a screenshot for AI feedback"
          onMouseEnter={() => { pasteHoverRef.current = true }}
          onMouseLeave={() => { pasteHoverRef.current = false }}
          onFocus={() => { pasteFocusRef.current = true }}
          onBlur={() => { pasteFocusRef.current = false }}
          className="inline-flex items-center gap-2 rounded-md border border-dashed border-muted-foreground/40 px-4 py-2 text-sm text-muted-foreground hover:border-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <ClipboardPaste className="h-4 w-4" />
          …or paste a screenshot
        </div>

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          aria-label="Take a picture for AI feedback"
          className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 ${linked ? 'border-primary bg-primary/10 font-medium text-primary ring-2 ring-primary/40' : 'border-dashed border-muted-foreground/40 text-muted-foreground hover:border-muted-foreground hover:text-foreground'}`}
        >
          <Camera className="h-4 w-4" />
          …or take a picture
        </button>
        {/* capture="environment" opens the rear camera on phones/tablets; on
            desktop it falls back to the file picker (webcam via the OS). */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            e.target.value = '' // allow re-selecting the same file
            if (file && !busy) void handlePastedBlob(file)
          }}
        />
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {(shownFeedback || status === 'streaming') && (
        <div className="mt-3 rounded-md border border-border bg-muted/30 p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Sparkles className="h-4 w-4" />
            AI feedback
            {status === 'streaming' && <Loader2 className="h-3 w-3 animate-spin" />}
          </div>
          {/* What was actually sent, so misreadings are visible, with the
              AI's marks on top */}
          {shownImage && <AIFeedbackMarks image={shownImage} marks={marks} />}
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
              {shownText}
            </ReactMarkdown>
          </div>
          {continuing && (
            <p className="mt-2 text-sm italic text-muted-foreground">
              Phew, overheated a little… grabbing more grey matter! (We AIs call it &ldquo;tokens&rdquo;.)
            </p>
          )}
        </div>
      )}

    </div>
    </PrintFallback>
    </div>
  )
}
